import { z } from 'zod';

import { UnitSystem } from '@optical/utils';

import { isoDatetimeToDate } from '../../codecs';
import { Position, playerGroupSchema, playerSchema } from '../../roster';
import { sessionWithPhasesSchema } from '../../sessions';
import { CourtLayout, Locale, thresholdsSchema } from '../../settings';
import { ShotLoad, shotEventSchema } from '../../statistics';
import { CourtArea } from '../court-area';

// ******************* Events *******************

export type SessionReportEvents = z.output<typeof sessionReportEventsSchema>;
export type SessionReportEventsDTO = z.input<typeof sessionReportEventsSchema>;

export const sessionReportEventsSchema = z.object({
  /**
   * All shot events for the entire session.
   * We always need to load all shots to display them in the session timeline.
   */
  allShots: z.array(shotEventSchema),
  /**
   * Filtered shot events for the selected time ranges (`null` if no time ranges are selected).
   */
  shots: z.array(shotEventSchema).nullable(),
});

// ******************* Stats *******************

export type SessionReportAreaShotLoadStats = z.output<typeof sessionReportAreaShotLoadStatsSchema>;
export type SessionReportAreaShotLoadStatsDTO = z.input<
  typeof sessionReportAreaShotLoadStatsSchema
>;

export const sessionReportAreaShotLoadStatsSchema = z.object({
  fga: z.number(),
  fgm: z.number(),
  fgp: z.number(),
  distr: z.number(),
  xvalue: z.number(),
});

export type SessionReportShotLoadStats = z.output<typeof sessionReportShotLoadStatsSchema>;
export type SessionReportShotLoadStatsDTO = z.input<typeof sessionReportShotLoadStatsSchema>;

export const sessionReportShotLoadStatsSchema = z.object({
  fga: z.number(),
  fgm: z.number(),
  fgp: z.number(),
  distr: z.number(),
  twopa: z.number(),
  twopm: z.number(),
  twopp: z.number(),
  threepa: z.number(),
  threepm: z.number(),
  threepp: z.number(),
});

export type SessionReportAreaShotStats = z.output<typeof sessionReportAreaShotStatsSchema>;
export type SessionReportAreaShotStatsDTO = z.input<typeof sessionReportAreaShotStatsSchema>;

export const sessionReportAreaShotStatsSchema = z.object({
  fga: z.number(),
  fgm: z.number(),
  fgp: z.number(),
  distr: z.number(),
  xvalue: z.number(),
});

export type SessionReportShotStats = z.output<typeof sessionReportShotStatsSchema>;
export type SessionReportShotStatsDTO = z.input<typeof sessionReportShotStatsSchema>;

export const sessionReportShotStatsSchema = z.object({
  fga: z.number(),
  fgm: z.number(),
  fgp: z.number(),
  twopa: z.number(),
  twopm: z.number(),
  twopp: z.number(),
  threepa: z.number(),
  threepm: z.number(),
  threepp: z.number(),
  fta: z.number(),
  ftm: z.number(),
  ftp: z.number(),
  efgp: z.number(),
});

export type SessionReportPerformanceStats = z.output<typeof sessionReportPerformanceStatsSchema>;
export type SessionReportPerformanceStatsDTO = z.input<typeof sessionReportPerformanceStatsSchema>;

export const sessionReportPerformanceStatsSchema = z.object({
  dist: z.number(),
  distpm: z.number(),
  load: z.number(),
  loadpm: z.number(),
  sprints: z.number(),
  sprintspm: z.number(),
  time: z.number(),
});

export type SessionReportStats = z.output<typeof sessionReportStatsSchema>;
export type SessionReportStatsDTO = z.input<typeof sessionReportStatsSchema>;

export const sessionReportStatsSchema = z.object({
  // Shot stats
  areaShotLoadStats: z.record(
    z.enum(CourtArea),
    z.record(z.enum(ShotLoad), sessionReportAreaShotLoadStatsSchema),
  ),
  areaShotStats: z.record(z.enum(CourtArea), sessionReportAreaShotStatsSchema),

  playerShotLoadStats: z.record(
    z.uuid(),
    z.record(z.enum(ShotLoad), sessionReportShotLoadStatsSchema),
  ),
  playerShotStats: z.record(z.uuid(), sessionReportShotStatsSchema),

  shotLoadStats: z.record(z.enum(ShotLoad), sessionReportShotLoadStatsSchema),
  shotStats: sessionReportShotStatsSchema,

  // Performance stats (`false` if not available, e.g. for IMU customers)
  performanceStats: z.union([sessionReportPerformanceStatsSchema, z.literal(false)]),
  playerPerformanceStats: z.union([
    z.record(z.uuid(), sessionReportPerformanceStatsSchema),
    z.literal(false),
  ]),
});

// ******************* Selection *******************

export type SessionReportPlayerSelection = z.output<typeof sessionReportPlayerSelectionSchema>;
export type SessionReportPlayerSelectionDTO = z.input<typeof sessionReportPlayerSelectionSchema>;

export const sessionReportPlayerSelectionSchema = z.object({
  wholeTeam: z.boolean(),
  group: playerGroupSchema.nullable(),
  players: z.array(playerSchema),
  position: z.enum(Position).nullable(),
});

export type SessionReportSessionSelection = z.output<typeof sessionReportSessionSelectionSchema>;
export type SessionReportSessionSelectionDTO = z.input<typeof sessionReportSessionSelectionSchema>;

export const sessionReportSessionSelectionSchema = z.object({
  session: sessionWithPhasesSchema,
});

export type SessionReportTimeRange = z.output<typeof sessionReportTimeRangeSchema>;
export type SessionReportTimeRangeDTO = z.input<typeof sessionReportTimeRangeSchema>;

export const sessionReportTimeRangeSchema = z.object({
  start: isoDatetimeToDate,
  end: isoDatetimeToDate,
});

export type SessionReportTimeRangeSelection = z.output<
  typeof sessionReportTimeRangeSelectionSchema
>;
export type SessionReportTimeRangeSelectionDTO = z.input<
  typeof sessionReportTimeRangeSelectionSchema
>;

export const sessionReportTimeRangeSelectionSchema = z.object({
  timeRanges: z.array(sessionReportTimeRangeSchema).nullable(),
});

export type SessionReportSelection = z.output<typeof sessionReportSelectionSchema>;
export type SessionReportSelectionDTO = z.input<typeof sessionReportSelectionSchema>;

export const sessionReportSelectionSchema = z.object({
  playerSelection: sessionReportPlayerSelectionSchema,
  sessionSelection: sessionReportSessionSelectionSchema,
  timeRangeSelection: sessionReportTimeRangeSelectionSchema,
});

// ******************* Settings *******************

export type SessionReportSettings = z.output<typeof sessionReportSettingsSchema>;
export type SessionReportSettingsDTO = z.input<typeof sessionReportSettingsSchema>;

export const sessionReportSettingsSchema = z.object({
  courtLayout: z.enum(CourtLayout),
  courtWidth: z.number().nullable(),
  courtLength: z.number().nullable(),
  locale: z.enum(Locale),
  thresholds: thresholdsSchema,
  unitSystem: z.enum(UnitSystem),
});

// ******************* Data *******************

export type SessionReportData = z.output<typeof sessionReportDataSchema>;
export type SessionReportDataDTO = z.input<typeof sessionReportDataSchema>;

export const sessionReportDataSchema = z.object({
  // Report data
  events: sessionReportEventsSchema,
  stats: sessionReportStatsSchema,

  // Metadata
  selection: sessionReportSelectionSchema,
  settings: sessionReportSettingsSchema,
});
